import { Injectable, isDevMode } from '@angular/core';
import { Observable } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import firebase from 'firebase/compat/app';
import { environment } from '../../environments/environment';
import { EducationService } from '../modules/education/education.service';
import { CounselingService } from '../modules/counseling/counseling.service';

const COLLECTION = 'AnnualImpactReport1';

export interface AdmissionStats {
  admission: number;
  discharge: number;
  followUpAdmission: number;
  followUpDischarge: number;
}

export interface SessionStats {
  individual: number;
  group: number;
  assessment: number;
  total: number;
}

export interface CumulativeStats {
  childrenSheltered: number;
  educationSessions: number;
  counsellingSessions: number;
  stressFreeNights: number;
  transitVehicles: number;
  totalKms: number;
  centres: number;
}

export interface DischargedChild {
  id: string;
  childCode: string;
  name: string;
  gender: string;
  age: number;
  dob: string;
  city: string;
  state: string;
  diagnosis: string;
  hospital: string;
  dischargeDate: Date;
  stayDuration: string;
}

export interface CaseStudyData {
  childCode: string;
  name: string;
  gender: string;
  age: number;
  dob: string;
  city: string;
  state: string;
  diagnosis: string;
  hospital: string;
  stayDuration: string;
  photos: string[];
  // Optional collage from the collage editor; when set, the report renders this single
  // composited image for the case study instead of the individual discharge photos.
  collageUrl?: string;
  educationHighlights: string;
  counsellingHighlights: string;
  story: string;
  teachingBullets: string[];
  counsellingBullets: string[];
  openingQuote: string;
  closingQuote: string;
}

export interface VolunteerEvent {
  title: string;
  date: string;
  location: string;
  activities: { heading: string; description: string }[];
  photos: string[];
  outcome: string;
  impact: string;
  conclusion: string;
}

/** Which major sections go into the generated PDF; false leaves that section out. */
export interface AnnualReportSections {
  cover: boolean;
  stats: boolean;
  nutrition: boolean;
  transport: boolean;
  counselling: boolean;
  teaching: boolean;
  caseStudies: boolean;
  events: boolean;
  contact: boolean;
}

export interface AnnualImpactReport {
  id?: string;
  centreCode: string;
  centreName: string;
  donorName: string;
  financialYear: string;
  period?: string;
  from: Date;
  to: Date;
  submitType: 'draft' | 'final';
  createdAt: any;
  updatedAt?: any;
  createdBy: string;
  cumulativeStats: CumulativeStats;
  admissionStats: AdmissionStats;
  educationStats: SessionStats;
  counsellingStats: SessionStats;
  nutritionNarrative: string;
  transportNarrative: string;
  counsellingNarrative: string;
  teachingNarrative: string;
  counsellingPhotos: string[];
  teachingPhotos: string[];
  // Optional collage from the collage editor, same convention as CaseStudyData.collageUrl above.
  counsellingCollageUrl?: string;
  teachingCollageUrl?: string;
  placeholderPhotos?: string[];
  coverTitle?: string;
  coverPhotoUrl?: string;
  contactPhotoUrl?: string;
  sections?: AnnualReportSections;
  headings?: Record<string, string>;
  // Highlights pulled from the year's Education / Counselling impact reports, given to the AI.
  teachingContext?: string;
  counsellingContext?: string;
  caseStudies: CaseStudyData[];
  volunteerEvents: VolunteerEvent[];
}

@Injectable({ providedIn: 'root' })
export class AnnualImpactReportService {

  private readonly env = isDevMode() ? environment.testMode : environment.productionMode;

  constructor(
    private afs: AngularFirestore,
    private education: EducationService,
    private counseling: CounselingService,
  ) {}

  async saveReport(data: Partial<AnnualImpactReport>): Promise<string> {
    const ref = await this.afs.collection(COLLECTION).add({
      ...data,
      createdAt: new Date(),
    });
    return ref.id;
  }

  async updateReport(id: string, data: Partial<AnnualImpactReport>): Promise<void> {
    await this.afs.collection(COLLECTION).doc(id).set(
      { ...data, updatedAt: new Date() },
      { merge: true }
    );
  }

  /** One saved report by id, or null if it doesn't exist. */
  async getReport(id: string): Promise<AnnualImpactReport | null> {
    const snap = await this.afs.collection(COLLECTION).doc(id).get().toPromise();
    return snap?.exists ? ({ ...(snap.data() as any), id } as AnnualImpactReport) : null;
  }

  getReports(centreCode?: string): Observable<AnnualImpactReport[]> {
    return this.afs.collection<AnnualImpactReport>(COLLECTION, ref => {
      const q = centreCode
        ? ref.where('centreCode', '==', centreCode).orderBy('createdAt', 'desc')
        : ref.orderBy('createdAt', 'desc');
      return q;
    }).valueChanges({ idField: 'id' });
  }

  // ── Data fetching (real Firestore queries) ─────────────────────────────────
  // Admission numbers + case-study candidates come from ALAdmissionCycles (env.admissionCycles,
  // one doc per stay). Session counts come from the pre-aggregated educationMonthlySummary /
  // counsellingMonthlySummary collections via EducationService/CounselingService, plus the
  // counselling assessments collection for the assessment count.

  /**
   * Admission / discharge counts for the period, split by first stay (cycleNumber 1)
   * vs. re-admission (cycleNumber > 1). Two single-field range queries (admission date,
   * discharge date) so no composite index is required; centre filter applied in memory.
   */
  async getAdmissionStats(centreCode: string, start: Date, end: Date): Promise<AdmissionStats> {
    const startTs = firebase.firestore.Timestamp.fromDate(this.startOfDay(start));
    const endTs = firebase.firestore.Timestamp.fromDate(this.endOfDay(end));
    const col = this.afs.firestore.collection(this.env.admissionCycles);

    const [admSnap, disSnap] = await Promise.all([
      col.where('dateOfAdmission', '>=', startTs).where('dateOfAdmission', '<=', endTs).get(),
      col.where('dateOfDischarge', '>=', startTs).where('dateOfDischarge', '<=', endTs).get(),
    ]);

    let admission = 0, followUpAdmission = 0, discharge = 0, followUpDischarge = 0;
    admSnap.docs.forEach(d => {
      const c = d.data() as any;
      if (!this.inCentre(c, centreCode)) return;
      ((c.cycleNumber ?? 1) > 1) ? followUpAdmission++ : admission++;
    });
    disSnap.docs.forEach(d => {
      const c = d.data() as any;
      if (!this.inCentre(c, centreCode)) return;
      ((c.cycleNumber ?? 1) > 1) ? followUpDischarge++ : discharge++;
    });

    return { admission, discharge, followUpAdmission, followUpDischarge };
  }

  /** Education sessions for the period from educationMonthlySummary (individual + group). */
  async getEducationSessionCounts(centreCode: string, start: Date, end: Date): Promise<SessionStats> {
    const docs = await this.education.getSummaryForDateRange(centreCode || 'All', start, end);
    let individual = 0, group = 0;
    for (const d of docs || []) {
      individual += Number((d as any).individualCount) || 0;
      group += Number((d as any).groupCount) || 0;
    }
    return { individual, group, assessment: 0, total: individual + group };
  }

  /**
   * Counselling sessions for the period: individual + group from counsellingMonthlySummary,
   * assessment count from the counselling assessments collection (filtered to the centre).
   */
  async getCounsellingSessionCounts(centreCode: string, start: Date, end: Date): Promise<SessionStats> {
    const [summaryDocs, assessments] = await Promise.all([
      this.counseling.getSummaryForDateRange(centreCode || 'All', start, end),
      this.counseling.getAssessmentsListByDateRange(this.startOfDay(start), this.endOfDay(end)),
    ]);
    let individual = 0, group = 0;
    for (const d of summaryDocs || []) {
      individual += Number((d as any).individualCount) || 0;
      group += Number((d as any).groupCount) || 0;
    }
    const assessment = (assessments || [])
      .filter((a: any) => this.inCentre(a, centreCode)).length;
    return { individual, group, assessment, total: individual + group + assessment };
  }

  /**
   * Children discharged in the period, one row per child (latest discharge kept),
   * newest first — the candidate pool for "Happy Discharge" case studies.
   */
  async getDischargedChildren(centreCode: string, start: Date, end: Date): Promise<DischargedChild[]> {
    const startTs = firebase.firestore.Timestamp.fromDate(this.startOfDay(start));
    const endTs = firebase.firestore.Timestamp.fromDate(this.endOfDay(end));
    const snap = await this.afs.firestore.collection(this.env.admissionCycles)
      .where('dateOfDischarge', '>=', startTs).where('dateOfDischarge', '<=', endTs).get();

    const byChild = new Map<string, DischargedChild>();
    snap.docs.forEach(doc => {
      const c = doc.data() as any;
      if (!this.inCentre(c, centreCode)) return;
      const dischargeDate = this.toDate(c.dateOfDischarge);
      if (!dischargeDate) return;

      const code = c.childCode || doc.id;
      const prev = byChild.get(code);
      if (prev && prev.dischargeDate >= dischargeDate) return;

      const admissionDate = this.toDate(c.dateOfAdmission);
      const dob = this.toDate(c.dob);
      byChild.set(code, {
        id: doc.id,
        childCode: code,
        name: (c.childName || `${c.firstName || ''} ${c.lastName || ''}`.trim()) || code,
        gender: c.gender || '',
        age: typeof c.age === 'number'
          ? c.age
          : (dob ? Math.max(0, Math.floor((dischargeDate.getTime() - dob.getTime()) / 31557600000)) : 0),
        dob: this.formatDate(dob),
        city: c.city || '',
        state: c.state || '',
        diagnosis: this.diagnosisText(c.diagnosis),
        hospital: c.hospital || '',
        dischargeDate,
        stayDuration: this.stayDurationText(admissionDate, dischargeDate),
      });
    });

    return Array.from(byChild.values())
      .sort((a, b) => b.dischargeDate.getTime() - a.dischargeDate.getTime());
  }

  /**
   * Organisation-wide cumulative totals for the "Overall Impact" header block.
   * Only the two with a clean bounded source are returned — education and counselling
   * sessions ever recorded (all centres, all time), from the monthly-summary collections.
   * childrenSheltered / stressFreeNights / totalKms have no single aggregate source and
   * stay as manual entry in the wizard.
   */
  async getCumulativeStats(): Promise<{ educationSessions: number; counsellingSessions: number }> {
    const [eduDocs, cslDocs] = await Promise.all([
      this.education.getSummaryForDateRange('All', null, null),
      this.counseling.getSummaryForDateRange('All', null, null),
    ]);
    const sumAll = (docs: any[]) => (docs || []).reduce((t, d) =>
      t + (Number(d.individualCount) || 0) + (Number(d.groupCount) || 0) + (Number(d.followUpCount) || 0), 0);
    return { educationSessions: sumAll(eduDocs), counsellingSessions: sumAll(cslDocs) };
  }

  // ── helpers ──────────────────────────────────────────────────────────────

  private inCentre(doc: any, centreCode: string): boolean {
    return !centreCode || centreCode === 'All' || doc?.centreCode === centreCode;
  }

  private startOfDay(d: Date): Date {
    return new Date(new Date(d).setHours(0, 0, 0, 0));
  }

  private endOfDay(d: Date): Date {
    return new Date(new Date(d).setHours(23, 59, 59, 999));
  }

  private toDate(v: any): Date | null {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v.toDate === 'function') { const d = v.toDate(); return isNaN(d.getTime()) ? null : d; }
    if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  private formatDate(d: Date | null): string {
    if (!d) return '';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
  }

  private stayDurationText(admission: Date | null, discharge: Date | null): string {
    if (!admission || !discharge) return '';
    const months = Math.round((discharge.getTime() - admission.getTime()) / 2629800000);
    if (months < 1) return 'Less than a month';
    return `${months} month${months === 1 ? '' : 's'}`;
  }

  private diagnosisText(dx: any): string {
    if (!dx) return '';
    if (Array.isArray(dx)) {
      return dx.map(x => (typeof x === 'string' ? x : (x?.name || x?.diagnosis || x?.label || '')))
        .filter(Boolean).join(', ');
    }
    return String(dx);
  }

}
